// 「今日の確認」の判定。数字から毎回計算し、友だち追加への影響が大きい順に並べる。
// 判定の基準を変えるときは、このファイルとテストだけを直す。

import { addDays, formatDay, type Period } from "./period";

export interface FunnelSummary {
  blogLandings: number;
  blogToLpClicks: number;
  lpViews: number;
  lineCtaClicks: number;
  lineFirstFollows: number;
}

export interface DailyFunnel extends FunnelSummary {
  day: string;
}

export interface SyncState {
  source_key: string;
  status: "running" | "succeeded" | "failed" | "skipped";
  message: string | null;
  started_at: string;
}

export type AlertSeverity = "problem" | "warning" | "good";

export type StepKey = "blog_to_lp" | "lp_to_cta" | "cta_to_follow";

export interface DashboardAlert {
  key: string;
  severity: AlertSeverity;
  title: string;
  detail: string;
  impact: { value: string; caption: string } | null;
  // 並べ替えに使う、1か月あたりの友だち追加への影響（人）。
  impactScore: number;
  chart: { kind: "step"; step: StepKey; previousRate: number } | { kind: "cta"; day: string } | null;
}

export const STEPS: Array<{ key: StepKey; from: keyof FunnelSummary; to: keyof FunnelSummary; label: string }> = [
  { key: "blog_to_lp", from: "blogLandings", to: "blogToLpClicks", label: "ブログからLPへの移動率" },
  { key: "lp_to_cta", from: "lpViews", to: "lineCtaClicks", label: "LPでLINEボタンが押される割合" },
  { key: "cta_to_follow", from: "lineCtaClicks", to: "lineFirstFollows", label: "LINEボタンから友だち追加になる割合" },
];

export const SYNC_SOURCE_LABELS: Record<string, string> = {
  wordpress: "記事一覧（WordPress）",
  gsc_blog: "検索の数字（Search Console）",
  ga4_blog: "まめぶろぐのGA4",
  ga4_lp: "LPのGA4",
};

// 比べる母数がこれより少ないと、割合の変化は偶然の揺れと区別できない。
const MIN_DENOMINATOR = 50;
// 前の期間より15%以上動いたら知らせる。
const RELATIVE_CHANGE = 0.15;
// 直前7日の平均がこの件数以上ある日に0件なら、計測かボタンの不具合を疑う。
const MIN_DAILY_CTA_FOR_ZERO_CHECK = 5;

export function rate(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null;
}

export function formatRate(value: number | null): string {
  return value === null ? "—" : `${(value * 100).toFixed(1)}%`;
}

function isEmpty(summary: FunnelSummary): boolean {
  return Object.values(summary).every((value) => !value);
}

function toMonthly(value: number, period: Period): number {
  return period.daysElapsed > 0 ? (value / period.daysElapsed) * 30 : value;
}

export function buildAlerts(input: {
  period: Period;
  today: string;
  current: FunnelSummary;
  previous: FunnelSummary;
  daily: DailyFunnel[];
  syncStates: SyncState[];
}): DashboardAlert[] {
  const { period, current, previous, daily, today } = input;
  const alerts: DashboardAlert[] = [];
  const periodKey = `${period.from}_${period.to.slice(0, 7)}`;

  if (isEmpty(current) && isEmpty(previous)) {
    alerts.push({
      key: `measurement-empty:${period.from.slice(0, 7)}`,
      severity: "warning",
      title: "計測データがまだ届いていません",
      detail: "本番のWordPressとLINEの計測がつながると、ここに判定が出ます。",
      impact: null,
      impactScore: 0,
      chart: null,
    });
  }

  for (const step of STEPS) {
    const currentDenominator = current[step.from];
    const previousDenominator = previous[step.from];
    if (currentDenominator < MIN_DENOMINATOR || previousDenominator < MIN_DENOMINATOR) continue;
    const currentRate = rate(current[step.to], currentDenominator)!;
    const previousRate = rate(previous[step.to], previousDenominator)!;
    if (previousRate <= 0) continue;
    const change = (currentRate - previousRate) / previousRate;
    if (Math.abs(change) < RELATIVE_CHANGE) continue;
    // この段階の割合が前の期間のままなら増えていた（減っていた）友だち追加を、1か月あたりに直す。
    const downstream = step.to === "lineFirstFollows"
      ? 1
      : rate(current.lineFirstFollows, current[step.to]) ?? rate(previous.lineFirstFollows, previous[step.to]) ?? 0;
    const monthly = Math.round(toMonthly(Math.abs(previousRate - currentRate) * currentDenominator * downstream, period));
    const down = change < 0;
    alerts.push({
      key: `step-${down ? "down" : "up"}:${step.key}:${periodKey}`,
      severity: down ? "problem" : "good",
      title: `${step.label}が${down ? "下がっています" : "上がっています"}`,
      detail: `${formatRate(previousRate)} → ${formatRate(currentRate)}（${period.kind === "month" ? "前月の同じ期間" : "前の期間"}と比べて）`,
      impact: monthly >= 1
        ? { value: `月に約${monthly.toLocaleString()}人`, caption: down ? "の友だち追加を逃している計算" : "の友だち追加が増えている計算" }
        : null,
      impactScore: monthly,
      chart: { kind: "step", step: step.key, previousRate },
    });
  }

  const byDay = new Map(daily.map((point) => [point.day, point]));
  for (let day = addDays(today, -1); day >= period.from && day >= addDays(today, -7); day = addDays(day, -1)) {
    const point = byDay.get(day);
    if (!point || point.lineCtaClicks > 0) continue;
    let sum = 0;
    for (let offset = 1; offset <= 7; offset += 1) sum += byDay.get(addDays(day, -offset))?.lineCtaClicks ?? 0;
    const average = sum / 7;
    if (average < MIN_DAILY_CTA_FOR_ZERO_CHECK) continue;
    const followRate = rate(current.lineFirstFollows, current.lineCtaClicks) ?? 0;
    const lost = Math.round(average * followRate);
    alerts.push({
      key: `cta-zero:${day}`,
      severity: "problem",
      title: `${formatDay(day)}、LINEボタンのクリックが0件でした`,
      detail: `直前7日間は1日平均${Math.round(average).toLocaleString()}件`,
      impact: lost >= 1 ? { value: `約${lost.toLocaleString()}人`, caption: "この1日の影響" } : null,
      impactScore: lost,
      chart: { kind: "cta", day },
    });
    break;
  }

  for (const state of input.syncStates) {
    if (state.status !== "failed" && state.status !== "skipped") continue;
    const label = SYNC_SOURCE_LABELS[state.source_key] ?? state.source_key;
    alerts.push({
      key: `sync:${state.source_key}:${state.started_at.slice(0, 10)}`,
      severity: "warning",
      title: `${label}の取り込みが止まっています`,
      detail: state.message ?? "取り込みに失敗しました。",
      impact: null,
      impactScore: 0,
      chart: null,
    });
  }

  // 良い兆しは対応が要らないので、影響の大きさにかかわらず最後に置く。
  const severityOrder: Record<AlertSeverity, number> = { problem: 0, warning: 1, good: 2 };
  const isGood = (alert: DashboardAlert) => (alert.severity === "good" ? 1 : 0);
  return alerts.sort((a, b) => isGood(a) - isGood(b) || b.impactScore - a.impactScore || severityOrder[a.severity] - severityOrder[b.severity]);
}
